/**
 * JS facade over the local chat history cache (`sync/chat_cache.rs`,
 * `chat_message_cache`, schema migration 8). Selecting a conversation renders
 * from SQLite instead of waiting on `GET /chat/channels/:id/messages`.
 *
 * ── The cache is a cache ────────────────────────────────────────────────────
 * The server is the source of truth, and this module is written so the cache
 * always loses a disagreement:
 *
 *  - Every failure here is a CACHE MISS, never an error the caller has to
 *    handle. Each function swallows and returns a neutral value, so the worst
 *    a broken cache can do is make chat behave exactly as it did before this
 *    existed: a REST round-trip.
 *  - Painting from disk NEVER replaces the verification fetch, and a buffer
 *    painted from disk stays UNVERIFIED until a fetch actually succeeds.
 *    `useChatRealtime` fetches on cold open even when the cached tail already
 *    looks current, because a server-side unsend does NOT advance the channel's
 *    `server_seq` — so "my cached tail equals the channel tip" is not evidence
 *    that nothing changed. The second half matters just as much: when that
 *    fetch FAILS, the disk-painted buffer must not be mistaken for a fetched
 *    one, or the WS join's gap-fill (`fillGapIfStale`) sees a non-empty buffer
 *    at the tip and returns early — leaving the session with no verification at
 *    all. `useChatRealtime` tracks verification per channel for exactly that.
 *    The cache removes the WAIT, not the request.
 *
 * ── Account scoping ─────────────────────────────────────────────────────────
 * Every call carries `getStorageNamespace()` — the same account id the Rust
 * side spells `sync::account_id_of` ('_local' when signed out; the two
 * constants are the same string on purpose). Rust refuses the call outright
 * unless the currently-open `apiboo.db` actually belongs to that account. See
 * `sync/chat_cache.rs`'s header for why a per-account database still needs an
 * account check: the DB swap on login is asynchronous and its failure is
 * swallowed at `syncAuthBridge.js:85`, so "signed in as B, A's file still
 * open" is a reachable state.
 *
 * ── Message bodies are stored in CLEARTEXT ──────────────────────────────────
 * `data` is the server's message JSON verbatim, `body` included, unencrypted on
 * disk. Owner decision, consistent with every other cached entity in
 * `apiboo.db` (collections, requests, environments). Note the asymmetry: the
 * server is concurrently moving `chat_messages.body` to Cloak encryption at
 * rest, so message text is encrypted server-side while written plainly here.
 * The full reasoning lives in migration 8's comment in `sync/schema.rs`.
 */
import { isTauri } from '../utils/tauri';
import { isServerPage } from '../utils/chatPageSpan';
import { getStorageNamespace } from './storageNamespace';

/**
 * How many rows to ask for on a cold open — an upper bound on what is handed to
 * the store, not an expectation. It matches `chatStore.js`'s in-memory `WINDOW`
 * so a full read can never overflow it, and Rust clamps it to
 * `CHANNEL_ROW_CAP` (500) regardless.
 *
 * In practice a channel holds far fewer than this: the only writer of history
 * pages is the verification fetch below, which asks for `{limit: 50}` and is
 * capped server-side at `@history_limit 50` anyway, and deep scrollback is
 * never cached — so a conversation older than the cache typically sits near 50
 * cached rows. Asking for 300 costs nothing and stops being a lie the day
 * either limit changes.
 */
export const CACHE_LOAD_LIMIT = 300;

async function invokeCache(cmd, args) {
  if (!isTauri()) return null; // web build has no apiboo.db
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke(cmd, { accountId: getStorageNamespace(), ...args });
}

/**
 * Shape one server message for the cache, or return null to REFUSE to cache it.
 *
 * Two refusals, both deliberate:
 *
 * 1. **No `server_seq` (or no `uid`) → not cached.** That is an optimistic
 *    message the server has not confirmed (see `chatStore.js`'s `seqOf`
 *    comment). It has no ordering key and may never be accepted; persisting it
 *    would put words on disk that were never sent. Rust's `server_seq NOT NULL`
 *    makes this structural too, but refusing here keeps it a no-op rather than
 *    a constraint error.
 *
 * 2. **A `secret` whose `expires_at` will not parse → not cached.** `kind:
 *    "secret"` messages are vault shares: the metadata carries the ciphertext
 *    AND the per-recipient wrapped keys, and the server sweeps them at
 *    `expires_at` (`Chat.SecretPurge`). If we cannot compute the deadline we
 *    cannot enforce it, and a null expiry means "never expires" downstream — so
 *    an unparseable timestamp must mean "do not put this on disk at all", not
 *    "keep it forever". Fail closed.
 *
 * `expires_at` is parsed here rather than in Rust because the server emits
 * RFC 3339 with a fractional-second part (`DateTime.to_iso8601` over a
 * `:utc_datetime_usec`) and the crate's only ISO-8601 reader,
 * `ceremony::unix_from_iso8601`, rejects fractions by design. `Date.parse`
 * handles it natively; the boundary then carries a plain integer.
 */
export function toCacheRow(message) {
  if (!message || message.server_seq == null || !message.uid) return null;

  let expiresAtMs = null;
  if (message.kind === 'secret') {
    const raw = message.metadata?.expires_at;
    if (raw != null && raw !== '') {
      const parsed = Date.parse(raw);
      if (!Number.isFinite(parsed)) return null; // fail closed — see above
      expiresAtMs = parsed;
    }
  }

  return {
    uid: String(message.uid),
    id: typeof message.id === 'number' ? message.id : null,
    serverSeq: message.server_seq,
    data: message,
    expiresAtMs,
  };
}

/**
 * Cached history for a channel, ascending by `server_seq`, or `[]` on any
 * miss/failure. Expired secrets are purged by Rust before this returns, so an
 * expired vault-share card is not served to a device that was offline past its
 * deadline.
 *
 * Precisely: that purge compares against `now_ms()`, the LOCAL wall clock. A
 * device whose clock is behind the deadline will still be served the card and
 * will render its wrapped keys. The unconditional guarantee is the server's —
 * `Chat.SecretPurge` scrubs the ciphertext and wrapped keys, so the next
 * successful fetch cannot re-cache them. Local expiry is what keeps an offline
 * device honest when its clock is, not a defence against a wrong one.
 */
export async function loadCachedHistory(channelId, limit = CACHE_LOAD_LIMIT) {
  if (!channelId) return [];
  try {
    const page = await invokeCache('chat_cache_load', { channelId, limit });
    return Array.isArray(page?.messages) ? page.messages : [];
  } catch {
    return []; // a cache that cannot answer is a cache miss, nothing more
  }
}

/**
 * Fold a fetched history page into the cache. This is the reconciliation step:
 * Rust upserts by `uid` (so edits overwrite) and deletes cached rows the page
 * omits, bounded by what that page's absences actually prove (`PageSpan` in
 * `sync/chat_cache.rs` — read it before changing this function).
 *
 * ── `reachesTip` ────────────────────────────────────────────────────────────
 * Declares that this page was fetched WITHOUT `before_seq`, i.e. it is the
 * newest visible run of messages in the channel. That is the difference between
 * "these were unsent" and "these are simply older than my cursor", and it is
 * required rather than inferred because the two look identical from here.
 *
 * Both current callers (`useChatRealtime`'s cold-open fetch and its gap-fill)
 * pass `true`. Deep scrollback — the only `beforeSeq` fetch in the app,
 * `ChatMessageView`'s scroll-to-top → `chatStore.prependMessages` — never
 * reaches this module at all, deliberately: it would be evicted by the
 * per-channel cap almost immediately and would push out the recent messages
 * that make the next launch fast. The default is therefore `false`, the
 * harmless reading, so a future caller that has not thought about tip-ness
 * cannot purge a channel by accident.
 *
 * ── `tipAtRequest` ──────────────────────────────────────────────────────────
 * The newest `server_seq` this device knew about at the moment it ISSUED the
 * request — the upper bound on what a tip page's absences may delete. Without
 * it a tip page speaks for `[min, ∞)` and deletes messages that arrived while
 * it was in flight, which on the send path is the message the user just typed.
 * `utils/chatPageSpan.js` carries the full reasoning; it is the same value the
 * in-memory buffer is reconciled with, passed through here so the two layers
 * apply one rule rather than two.
 *
 * ── `exemptUids` ────────────────────────────────────────────────────────────
 * The uids that arrived LIVE while this page was in flight. The bound above is
 * a proxy for "did this message exist when I asked", and on the cold-open path
 * it is derived from a disk read that executes AFTER the request went out — by
 * which time `cacheMessage` may already have written a live arrival into this
 * very table, lifting the bound over the row it was meant to protect. These
 * uids are therefore excluded from the deletion outright, on both layers.
 *
 * ── An empty page is not a failed fetch ─────────────────────────────────────
 * `messages: []` from a TIP fetch is the strongest deletion signal this cache
 * ever receives — the channel has no visible messages left — and it is
 * forwarded so Rust can purge the channel. A fetch that FAILED is the opposite
 * fact and must purge nothing, so it is kept out structurally rather than by
 * convention: the caller's `.catch` never reaches this function, and anything
 * that is not an actual array (a rejected promise's `undefined`, a malformed
 * response) is refused by `isServerPage` — the same predicate
 * `chatStore.reconcileMessages` uses, so the buffer and the disk cannot
 * disagree about what counts as a page. Only a real, empty array purges.
 */
export async function cacheHistoryPage(
  channelId,
  messages,
  { reachesTip = false, tipAtRequest = 0, exemptUids = [] } = {}
) {
  // NOT `!messages.length` — an empty ARRAY is a fact about the channel; a
  // non-array is the absence of an answer. See above.
  if (!channelId || !isServerPage(messages)) return;
  const rows = messages.map(toCacheRow).filter(Boolean);
  // A page of only-optimistic (or otherwise unstorable) rows is not evidence
  // that the server's page was empty, so it must not be forwarded as one.
  if (rows.length === 0 && messages.length > 0) return;
  // An empty page carries information ONLY when it reaches the tip. An empty
  // `before_seq` page is a no-op on the Rust side too (`PageSpan::Interior`
  // with no contents deletes nothing), so this skips a pointless IPC rather
  // than changing any semantics — do not widen it to `rows.length === 0`.
  if (rows.length === 0 && !reachesTip) return;
  // An empty tip page from a plan-WINDOWED history (Free: 90 days) says
  // nothing about what exists — the messages are on the server, this account
  // may simply not read them. Forwarding it would make Rust delete the only
  // copy the device can still show offline. Same rule as
  // `pageDeletionSpan`'s `windowed` branch, applied where the DELETE happens.
  if (rows.length === 0 && Number.isFinite(messages?.historyDays) && messages.historyDays > 0) return;
  try {
    await invokeCache('chat_cache_reconcile', {
      channelId,
      messages: rows,
      reachesTip,
      // `isInteger`, not `isFinite`: the far side is serde's `Option<i64>`,
      // which refuses a fractional number and fails the WHOLE reconcile —
      // upserts and deletions alike — into this function's `catch {}`. A
      // non-integer here means "I do not know what I knew", whose safe reading
      // is 0 (the page speaks only for its own span), not a silent no-op.
      knownTip: Number.isInteger(tipAtRequest) ? tipAtRequest : 0,
      exemptUids: (Array.isArray(exemptUids) ? exemptUids : []).map(String),
    });
  } catch {
    /* best-effort: a cache write that fails costs a REST fetch next launch */
  }
}

/**
 * Cache (or update) one message — a live arrival, an edit, or a reaction
 * change. Explicitly NOT a tip page: a live event says nothing about what else
 * does or does not exist in the channel. With `reachesTip: false` the
 * reconcile's deletion span is this message's own seq, which it occupies, so
 * this can never remove anything else.
 */
export async function cacheMessage(channelId, message) {
  const row = toCacheRow(message);
  if (!channelId || !row) return;
  try {
    await invokeCache('chat_cache_reconcile', {
      channelId,
      messages: [row],
      reachesTip: false,
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Purge one message on `message_deleted`. The event payload is
 * `{ id, server_seq }` and carries no uid, which is why the server row id is
 * stored alongside it.
 *
 * This is only HALF of deletion handling — the half that requires the app to
 * have been running. `cacheHistoryPage`'s reconciliation is the other half and
 * the one that covers a device that was closed when the unsend happened.
 */
export async function forgetCachedMessage(channelId, id) {
  if (!channelId || id == null) return;
  try {
    await invokeCache('chat_cache_delete', { channelId, id });
  } catch {
    /* best-effort */
  }
}

/** Drop a channel's whole cache — left, or removed from, the conversation. */
export async function forgetCachedChannel(channelId) {
  if (!channelId) return;
  try {
    await invokeCache('chat_cache_clear', { channelId });
  } catch {
    /* best-effort */
  }
}

/**
 * Startup expiry sweep across every cached channel. Read-time purging in
 * `loadCachedHistory` already guarantees nothing expired is ever SERVED; this
 * is what stops an expired secret's ciphertext and wrapped keys from sitting on
 * disk indefinitely in a conversation the user simply never reopens.
 */
export async function sweepExpiredCache() {
  try {
    if (!isTauri()) return 0;
    const { invoke } = await import('@tauri-apps/api/core');
    return (await invoke('chat_cache_sweep')) || 0;
  } catch {
    return 0;
  }
}
